import React, { useState } from 'react';
import { useQuery } from 'react-query';
import api from '../api';

const fetchSummary = (days) =>
  api.get(`/analytics/summary/?days=${days}`).then(r => r.data);

const fetchUserActivity = (username, days) =>
  api.get(`/analytics/user-activity/?username=${encodeURIComponent(username)}&days=${days}`).then(r => r.data);

function fmt(ts) {
  if (!ts) return '—';
  const d = new Date(ts);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
    + ' ' + d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

function statusColor(code) {
  if (code >= 500) return '#ff6b6b';
  if (code >= 400) return '#ffa94d';
  return 'var(--text-muted)';
}

function Widget({ title, count, children }) {
  return (
    <div style={{
      background: 'var(--bg2)', border: '1px solid var(--border)',
      borderRadius: 10, overflow: 'hidden', display: 'flex', flexDirection: 'column',
    }}>
      <div style={{
        padding: '10px 16px', borderBottom: '1px solid var(--border)',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <span style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase',
                        letterSpacing: 1, color: 'var(--text-muted)' }}>{title}</span>
        {count != null && (
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{count} total</span>
        )}
      </div>
      <div style={{ overflowY: 'auto', maxHeight: 320 }}>
        {children}
      </div>
    </div>
  );
}

function WidgetRow({ cols }) {
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: cols.map(c => c.width || '1fr').join(' '),
      padding: '6px 16px', borderBottom: '1px solid var(--border)',
      fontSize: 13, alignItems: 'center', gap: 8,
    }}>
      {cols.map((c, i) => (
        <span key={i} style={{
          color: c.muted ? 'var(--text-muted)' : c.color || 'var(--text)',
          fontWeight: c.bold ? 600 : 400,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }} title={String(c.label ?? '')}>
          {c.label ?? '—'}
        </span>
      ))}
    </div>
  );
}

function EmptyRow({ msg }) {
  return (
    <div style={{ padding: '20px 16px', textAlign: 'center',
                   color: 'var(--text-muted)', fontSize: 13 }}>{msg}</div>
  );
}

function SummaryCard({ label, value, sub }) {
  return (
    <div style={{
      background: 'var(--bg2)', borderRadius: 8, padding: '14px 20px',
      border: '1px solid var(--border)',
    }}>
      <div style={{ color: 'var(--text-muted)', fontSize: 11, textTransform: 'uppercase',
                     letterSpacing: 1, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 700 }}>{value}</div>
      {sub && <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function UserActivityPanel({ username, days }) {
  const { data, isLoading, error } = useQuery(
    ['user-activity', username, days],
    () => fetchUserActivity(username, days),
    { enabled: !!username }
  );

  if (!username) return null;
  if (isLoading) return (
    <div style={{ gridColumn: '1 / -1', padding: 20, color: 'var(--text-muted)', textAlign: 'center' }}>
      Loading activity for {username}…
    </div>
  );
  if (error) return (
    <div style={{ gridColumn: '1 / -1', padding: 20, color: '#ff6b6b', textAlign: 'center' }}>
      Failed to load user activity.
    </div>
  );

  return (
    <div style={{ gridColumn: '1 / -1' }}>
      {/* User stat cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 16 }}>
        <SummaryCard label="Plays"     value={data.total_plays.toLocaleString()} />
        <SummaryCard label="Skips"     value={data.total_skips.toLocaleString()} />
        <SummaryCard label="Logins"    value={data.total_logins.toLocaleString()} />
        <SummaryCard label="API Calls" value={data.total_api_calls.toLocaleString()} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        {/* Top tracks for user */}
        <Widget title={`Top Tracks — ${username}`}>
          {data.top_tracks.length === 0
            ? <EmptyRow msg="No plays in this period" />
            : data.top_tracks.map((t, i) => (
              <WidgetRow key={i} cols={[
                { label: `${i + 1}.`, muted: true, width: '28px' },
                { label: t.track__title, bold: true, width: '1fr' },
                { label: t.track__artist__name, muted: true, width: '130px' },
                { label: `${t.count}×`, width: '40px' },
              ]} />
            ))}
        </Widget>

        {/* Top artists for user */}
        <Widget title={`Top Artists — ${username}`}>
          {data.top_artists.length === 0
            ? <EmptyRow msg="No plays in this period" />
            : data.top_artists.map((a, i) => (
              <WidgetRow key={i} cols={[
                { label: `${i + 1}.`, muted: true, width: '28px' },
                { label: a.track__artist__name, bold: true, width: '1fr' },
                { label: `${a.count}×`, width: '40px' },
              ]} />
            ))}
        </Widget>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 16 }}>
        {/* Recent plays */}
        <Widget title="Recent Plays">
          {data.recent_plays.length === 0
            ? <EmptyRow msg="No plays" />
            : data.recent_plays.map((p, i) => (
              <WidgetRow key={i} cols={[
                { label: p.track__title, bold: true, width: '1fr' },
                { label: fmt(p.timestamp), muted: true, width: '110px' },
              ]} />
            ))}
        </Widget>

        {/* Recent skips */}
        <Widget title="Recent Skips">
          {data.recent_skips.length === 0
            ? <EmptyRow msg="No skips" />
            : data.recent_skips.map((s, i) => (
              <WidgetRow key={i} cols={[
                { label: s.track__title, bold: true, width: '1fr' },
                { label: fmt(s.timestamp), muted: true, width: '110px' },
              ]} />
            ))}
        </Widget>

        {/* Recent pages */}
        <Widget title="Recent Pages">
          {data.recent_pages.length === 0
            ? <EmptyRow msg="No page views" />
            : data.recent_pages.map((p, i) => (
              <WidgetRow key={i} cols={[
                { label: p.page || '/', bold: true, width: '1fr' },
                { label: fmt(p.timestamp), muted: true, width: '110px' },
              ]} />
            ))}
        </Widget>

        {/* Recent API calls */}
        <Widget title="Recent API Calls">
          {data.recent_api.length === 0
            ? <EmptyRow msg="No API calls" />
            : data.recent_api.map((c, i) => (
              <WidgetRow key={i} cols={[
                { label: c.method, bold: true, color: 'var(--accent)', width: '45px' },
                { label: c.path, width: '1fr' },
                { label: String(c.status_code), color: statusColor(c.status_code), width: '40px' },
                { label: fmt(c.timestamp), muted: true, width: '110px' },
              ]} />
            ))}
        </Widget>
      </div>
    </div>
  );
}

export default function Analytics() {
  const [days, setDays] = useState(30);
  const [selectedUser, setSelectedUser] = useState('');

  const { data, isLoading } = useQuery(['analytics', days], () => fetchSummary(days),
    { keepPreviousData: true });

  if (isLoading) return <div style={{ padding: 24, color: 'var(--text-muted)' }}>Loading…</div>;

  const totalPlays = data.plays_by_day.reduce((s, d) => s + d.count, 0);
  const mobile  = data.device_split.find(d => d.device === 'mobile')?.count || 0;
  const desktop = data.device_split.find(d => d.device === 'desktop')?.count || 0;
  const total   = mobile + desktop || 1;

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1200 }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
        <h2 style={{ margin: 0 }}>Analytics</h2>
        <div style={{ display: 'flex', gap: 6 }}>
          {[7, 30, 90].map(d => (
            <button key={d} onClick={() => setDays(d)} style={{
              padding: '4px 12px', borderRadius: 4, cursor: 'pointer', fontSize: 13,
              background: days === d ? 'var(--accent)' : 'var(--bg2)',
              color: days === d ? '#fff' : 'var(--text)',
              border: `1px solid ${days === d ? 'var(--accent)' : 'var(--border)'}`,
            }}>
              {d}d
            </button>
          ))}
        </div>
      </div>

      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 28 }}>
        <SummaryCard label="Total Plays"    value={totalPlays.toLocaleString()} />
        <SummaryCard label="Active Users"   value={data.active_users.length} />
        <SummaryCard label="Logins"         value={data.recent_logins.length} sub={`last ${days}d`} />
        <SummaryCard label="API Calls"      value={data.recent_api_calls.length.toLocaleString()} sub={`last ${days}d`} />
        <SummaryCard label="Mobile / Desktop"
          value={`${Math.round(mobile / total * 100)}% / ${Math.round(desktop / total * 100)}%`} />
      </div>

      {/* Widgets grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>

        {/* User Activity selector */}
        <div style={{ gridColumn: '1 / -1' }}>
          <div style={{
            background: 'var(--bg2)', border: '1px solid var(--border)',
            borderRadius: 10, padding: '14px 16px',
            display: 'flex', alignItems: 'center', gap: 12,
          }}>
            <span style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase',
                            letterSpacing: 1, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
              User Activity
            </span>
            <select
              value={selectedUser}
              onChange={e => setSelectedUser(e.target.value)}
              style={{
                background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 6,
                color: 'var(--text)', padding: '5px 10px', fontSize: 13, cursor: 'pointer',
              }}
            >
              <option value="">— Select a user —</option>
              {(data.all_users || []).map(u => (
                <option key={u.username} value={u.username}>{u.username}</option>
              ))}
            </select>
            {selectedUser && (
              <button
                onClick={() => setSelectedUser('')}
                style={{ background: 'none', border: 'none', color: 'var(--text-muted)',
                          cursor: 'pointer', fontSize: 13 }}
              >
                ✕ Clear
              </button>
            )}
          </div>
        </div>

        {/* User activity panels — only shown when a user is selected */}
        <UserActivityPanel username={selectedUser} days={days} />

        {/* Recent Logins */}
        <Widget title="Recent Logins" count={data.recent_logins.length}>
          {data.recent_logins.length === 0
            ? <EmptyRow msg="No logins in this period" />
            : data.recent_logins.map((l, i) => (
              <WidgetRow key={i} cols={[
                { label: l.user__username || '—', bold: true, width: '140px' },
                { label: l.device, muted: true, width: '80px' },
                { label: fmt(l.timestamp), muted: true },
              ]} />
            ))}
        </Widget>

        {/* Recent Page Views */}
        <Widget title="Recent Page Views" count={data.recent_pageviews.length}>
          {data.recent_pageviews.length === 0
            ? <EmptyRow msg="No page views in this period" />
            : data.recent_pageviews.map((p, i) => (
              <WidgetRow key={i} cols={[
                { label: p.user__username || '—', bold: true, width: '120px' },
                { label: p.page || '/', width: '160px' },
                { label: p.device, muted: true, width: '70px' },
                { label: fmt(p.timestamp), muted: true },
              ]} />
            ))}
        </Widget>

        {/* Recent Plays */}
        <Widget title="Recent Song Plays" count={data.recent_plays.length}>
          {data.recent_plays.length === 0
            ? <EmptyRow msg="No plays in this period" />
            : data.recent_plays.map((p, i) => (
              <WidgetRow key={i} cols={[
                { label: p.user__username || '—', bold: true, width: '110px' },
                { label: p.track__title, width: '1fr' },
                { label: p.track__artist__name, muted: true, width: '120px' },
                { label: fmt(p.timestamp), muted: true, width: '120px' },
              ]} />
            ))}
        </Widget>

        {/* Recent Skips */}
        <Widget title="Recent Skips" count={data.recent_skips.length}>
          {data.recent_skips.length === 0
            ? <EmptyRow msg="No skips in this period" />
            : data.recent_skips.map((s, i) => (
              <WidgetRow key={i} cols={[
                { label: s.user__username || '—', bold: true, width: '110px' },
                { label: s.track__title, width: '1fr' },
                { label: s.track__artist__name, muted: true, width: '120px' },
                { label: fmt(s.timestamp), muted: true, width: '120px' },
              ]} />
            ))}
        </Widget>

        {/* Recent API Calls — full width */}
        <div style={{ gridColumn: '1 / -1' }}>
          <Widget title="Recent API Calls" count={data.recent_api_calls.length}>
            {data.recent_api_calls.length === 0
              ? <EmptyRow msg="No API calls logged yet — restart the backend to activate middleware" />
              : data.recent_api_calls.map((c, i) => (
                <WidgetRow key={i} cols={[
                  { label: c.user__username || '—', bold: true, width: '110px' },
                  { label: c.method, bold: true, color: 'var(--accent)', width: '55px' },
                  { label: c.path + (c.query_string ? '?' + c.query_string : ''), width: '1fr' },
                  { label: String(c.status_code), color: statusColor(c.status_code), width: '50px' },
                  { label: c.response_ms != null ? `${c.response_ms}ms` : '—', muted: true, width: '65px' },
                  { label: fmt(c.timestamp), muted: true, width: '130px' },
                ]} />
              ))}
          </Widget>
        </div>

        {/* Top Tracks */}
        <Widget title="Top Tracks">
          {data.top_tracks.length === 0
            ? <EmptyRow msg="No plays in this period" />
            : data.top_tracks.map((t, i) => (
              <WidgetRow key={t.track__id} cols={[
                { label: `${i + 1}.`, muted: true, width: '30px' },
                { label: t.track__title, bold: true, width: '1fr' },
                { label: t.track__artist__name, muted: true, width: '140px' },
                { label: `${t.count}×`, width: '45px' },
              ]} />
            ))}
        </Widget>

        {/* Top Albums */}
        <Widget title="Top Albums">
          {data.top_albums.length === 0
            ? <EmptyRow msg="No plays in this period" />
            : data.top_albums.map((a, i) => (
              <WidgetRow key={i} cols={[
                { label: `${i + 1}.`, muted: true, width: '30px' },
                { label: a.track__album__title, bold: true, width: '1fr' },
                { label: a.track__album__artist__name, muted: true, width: '140px' },
                { label: `${a.count}×`, width: '45px' },
              ]} />
            ))}
        </Widget>

        {/* Most Skipped */}
        <Widget title="Most Skipped">
          {data.most_skipped.length === 0
            ? <EmptyRow msg="No skips in this period" />
            : data.most_skipped.map((t, i) => (
              <WidgetRow key={t.track__id} cols={[
                { label: `${i + 1}.`, muted: true, width: '30px' },
                { label: t.track__title, bold: true, width: '1fr' },
                { label: t.track__artist__name, muted: true, width: '140px' },
                { label: `${t.count}×`, width: '45px' },
              ]} />
            ))}
        </Widget>

        {/* Top Artists */}
        <Widget title="Top Artists">
          {data.top_artists.length === 0
            ? <EmptyRow msg="No plays in this period" />
            : data.top_artists.map((a, i) => (
              <WidgetRow key={a.track__artist__name} cols={[
                { label: `${i + 1}.`, muted: true, width: '30px' },
                { label: a.track__artist__name, bold: true, width: '1fr' },
                { label: `${a.count}×`, width: '45px' },
              ]} />
            ))}
        </Widget>

        {/* Active Users */}
        <Widget title="Active Users">
          {data.active_users.length === 0
            ? <EmptyRow msg="No activity in this period" />
            : data.active_users.map(u => (
              <WidgetRow key={u.user__username} cols={[
                { label: u.user__username, bold: true, width: '1fr' },
                { label: `${u.plays} plays`, width: '80px' },
              ]} />
            ))}
        </Widget>

        {/* Top Pages */}
        <Widget title="Top Pages">
          {data.top_pages.length === 0
            ? <EmptyRow msg="No page views in this period" />
            : data.top_pages.map(p => (
              <WidgetRow key={p.page} cols={[
                { label: p.page || '/', bold: true, width: '1fr' },
                { label: `${p.count}×`, width: '50px' },
              ]} />
            ))}
        </Widget>

        {/* Top API Endpoints */}
        <Widget title="Top API Endpoints">
          {data.top_api_paths.length === 0
            ? <EmptyRow msg="No API calls logged yet" />
            : data.top_api_paths.map((p, i) => (
              <WidgetRow key={i} cols={[
                { label: p.method, bold: true, color: 'var(--accent)', width: '55px' },
                { label: p.path, width: '1fr' },
                { label: `${p.count}×`, width: '50px' },
                { label: p.avg_ms != null ? `${Math.round(p.avg_ms)}ms avg` : '—', muted: true, width: '80px' },
              ]} />
            ))}
        </Widget>

        {/* Plays by Day — full width bar chart */}
        <div style={{ gridColumn: '1 / -1' }}>
          <Widget title="Plays by Day">
            {data.plays_by_day.length === 0
              ? <EmptyRow msg="No plays in this period" />
              : (
                <div style={{ padding: '12px 16px' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 80 }}>
                    {(() => {
                      const max = Math.max(...[...data.plays_by_day].map(x => x.count), 1);
                      return [...data.plays_by_day].reverse().map(d => {
                        const px = Math.max(2, Math.round((d.count / max) * 76));
                        return (
                          <div key={d.day} style={{ flex: 1, display: 'flex', alignItems: 'flex-end',
                                                     height: 80 }} title={`${d.day}: ${d.count}`}>
                            <div style={{ width: '100%', background: 'var(--accent)',
                                           height: px, borderRadius: 2, opacity: 0.8 }} />
                          </div>
                        );
                      });
                    })()}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between',
                                 marginTop: 6, fontSize: 11, color: 'var(--text-muted)' }}>
                    <span>{[...data.plays_by_day].reverse()[0]?.day}</span>
                    <span>{data.plays_by_day[0]?.day}</span>
                  </div>
                </div>
              )}
          </Widget>
        </div>

      </div>
    </div>
  );
}
